import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { InputTextarea } from 'primereact/inputtextarea';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useUpdateVehicleStockSellableMutation } from '../../../../api/endpoints/vehicleOrder';
import { toastService } from '../../../../utils/toastService';
import { VEHICLE_STOCK_SELLABLE } from '../../vehiclesForSale/utils/statusBadges';

const SellableActionDialog = ({ visible, row, onHide, onActionComplete }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();

    const [sellable, setSellable] = useState(VEHICLE_STOCK_SELLABLE.UNSPECIFIED);
    const [description, setDescription] = useState('');
    const [validationError, setValidationError] = useState('');

    const [updateSellable, { isLoading }] = useUpdateVehicleStockSellableMutation();

    useEffect(() => {
        if (visible && row) {
            setSellable(row.sellable ?? VEHICLE_STOCK_SELLABLE.UNSPECIFIED);
            setDescription('');
            setValidationError('');
        }
    }, [visible, row]);

    if (!row) return null;

    const isForSale = sellable === VEHICLE_STOCK_SELLABLE.FOR_SALE;
    const showDescription = isForSale;

    const sellableOptions = [
        { label: th('vehicleSalesMyVehicleStock.sellable.unspecified'), value: VEHICLE_STOCK_SELLABLE.UNSPECIFIED },
        { label: th('vehicleSalesMyVehicleStock.sellable.notForSale'), value: VEHICLE_STOCK_SELLABLE.NOT_FOR_SALE },
        { label: th('vehicleSalesMyVehicleStock.sellable.forSale'), value: VEHICLE_STOCK_SELLABLE.FOR_SALE }
    ];

    const handleSave = async () => {
        if (sellable == null) {
            setValidationError(th('vehicleSalesMyVehicleStock.validation.sellableRequired'));
            return;
        }
        setValidationError('');

        try {
            const body = { stockId: row.id, sellable, languageId };
            if (showDescription && description?.trim()) {
                body.description = description.trim();
            }
            const res = await updateSellable(body).unwrap();
            if (res?.isSuccess || res?.isSuccessful) {
                toastService.success(
                    res.message || th('vehicleSalesMyVehicleStock.toast.sellableUpdated')
                );
                onActionComplete?.();
                onHide();
            }
        } catch (err) {
            void err;
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label={th('vehicleSalesMyVehicleStock.buttons.cancel')}
                icon="pi pi-times"
                className="p-button-outlined"
                onClick={onHide}
                disabled={isLoading}
            />
            <Button
                label={th('vehicleSalesMyVehicleStock.buttons.save')}
                icon="pi pi-check"
                onClick={handleSave}
                loading={isLoading}
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={th('vehicleSalesMyVehicleStock.dialog.updateSellableTitle')}
            style={{ width: '480px' }}
            modal
            footer={footer}
            closable={!isLoading}
        >
            <div className="field mb-3">
                <div className="text-sm mb-2 font-medium">{row.chassisNo}</div>
                <div className="text-sm text-600">
                    {row.brand} · {row.model} · {row.maktx}
                </div>
            </div>

            <div className="field mb-3">
                <label className="block mb-2 text-sm font-medium">
                    {th('vehicleSalesMyVehicleStock.columns.sellable')}
                </label>
                <div className="flex flex-column gap-2">
                    {sellableOptions.map((opt) => (
                        <div key={opt.value} className="flex align-items-center">
                            <RadioButton
                                inputId={`sellable-${opt.value}`}
                                name="sellable"
                                value={opt.value}
                                checked={sellable === opt.value}
                                onChange={(e) => setSellable(e.value)}
                            />
                            <label
                                htmlFor={`sellable-${opt.value}`}
                                className="ml-2 cursor-pointer"
                            >
                                {opt.label}
                            </label>
                        </div>
                    ))}
                </div>
                {validationError && (
                    <small className="p-error block mt-1">{validationError}</small>
                )}
            </div>

            {showDescription && (
                <div className="field mb-3">
                    <label className="block mb-1 text-sm">
                        {th('vehicleSalesMyVehicleStock.dialog.description')}
                    </label>
                    <InputTextarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        className="w-full"
                        placeholder={th('vehicleSalesMyVehicleStock.dialog.descriptionPlaceholder')}
                        maxLength={500}
                    />
                </div>
            )}
        </Dialog>
    );
};

export default SellableActionDialog;
